import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';
import { useLanguage } from '../../../contexts/LanguageContext';

const DocumentGenerationPanel = ({ 
  isOpen, 
  onClose, 
  category, 
  currentLanguage,
  onGenerate 
}) => {
  const { t } = useLanguage();
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [selectedGroup, setSelectedGroup] = useState('');
  const [selectedLanguage, setSelectedLanguage] = useState('fr');
  const [includeSignature, setIncludeSignature] = useState(false);
  const [bulkGeneration, setBulkGeneration] = useState(false);

  if (!isOpen || !category) return null;

  const templateOptions = [
    { value: 'standard', label: t('documents.standardTemplate') },
    { value: 'detailed', label: t('documents.detailedTemplate') },
    { value: 'simplified', label: t('documents.simplifiedTemplate') }
  ];

  const groupOptions = [
    { value: 'omra-2026-01', label: 'Omra Janvier 2026 - Groupe A' },
    { value: 'omra-2026-02', label: 'Omra Février 2026 - Groupe B' },
    { value: 'hajj-2026', label: 'Hajj 2026 - Groupe Principal' }
  ];

  const languageOptions = [
    { value: 'fr', label: t('documents.french') },
    { value: 'ar', label: t('documents.arabic') },
    { value: 'both', label: t('documents.both') }
  ];

  const handleGenerate = () => {
    onGenerate({
      category: category?.id,
      template: selectedTemplate,
      group: selectedGroup,
      language: selectedLanguage,
      includeSignature,
      bulkGeneration
    });
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-card border border-border rounded-lg shadow-warm-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-card border-b border-border p-4 md:p-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-lg ${category?.bgColor} flex items-center justify-center`}>
              <Icon name={category?.icon} size={20} className={category?.color} />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-foreground">
                {t('documents.generateDocuments')}
              </h2>
              <p className="caption text-muted-foreground">
                {category?.name?.[currentLanguage]}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-md hover:bg-muted transition-smooth"
            aria-label="Close"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        <div className="p-4 md:p-6 space-y-6">
          <Select
            label={t('documents.documentTemplate')}
            description={t('documents.chooseFormat')}
            options={templateOptions}
            value={selectedTemplate}
            onChange={setSelectedTemplate}
            required
          />

          <Select
            label={t('documents.pilgrimGroup')}
            description={t('documents.selectGroup')}
            options={groupOptions}
            value={selectedGroup}
            onChange={setSelectedGroup}
            searchable
            required
          />

          <Select
            label={t('documents.documentLanguage')}
            options={languageOptions}
            value={selectedLanguage}
            onChange={setSelectedLanguage}
            required
          />

          <div className="space-y-3">
            <Checkbox
              label={t('documents.includeSignature')}
              description={t('documents.addWorkflow')}
              checked={includeSignature}
              onChange={(e) => setIncludeSignature(e?.target?.checked)}
            />

            <Checkbox
              label={t('documents.bulkGeneration')}
              description={t('documents.generateForAll')}
              checked={bulkGeneration}
              onChange={(e) => setBulkGeneration(e?.target?.checked)}
            />
          </div>

          <div className="bg-accent/10 border border-accent/20 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <Icon name="Info" size={20} className="text-accent flex-shrink-0 mt-0.5" />
              <div className="text-sm text-foreground">
                <p className="font-medium mb-1">
                  {t('documents.importantInfo')}
                </p>
                <p className="text-muted-foreground">
                  {t('documents.autoSaveInfo')}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="sticky bottom-0 bg-card border-t border-border p-4 md:p-6 flex items-center justify-end gap-3">
          <Button
            variant="outline"
            onClick={onClose}
          >
            {t('documents.cancel')}
          </Button>
          <Button
            variant="default"
            iconName="FileText"
            iconPosition="left"
            onClick={handleGenerate}
            disabled={!selectedTemplate || !selectedGroup}
          >
            {t('documents.generate')}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default DocumentGenerationPanel;